/**
 * @fileoverview
 * Renders letter box missions specific table
 * Handles logic for formatting data
 * Uses SimpleTableTable component for table rendering
 */
import React, { useMemo } from 'react'
import SimpleTableTable from '../../../../../Components/SimpleTableTable'
import { tableHeaders, sortableColumns, filterableColumns } from '../settings'
import { getFooterData, formatData } from '../util'
import letterboxMissionsTableRowRenderer from '../blocks/letterboxMissionsTableRowRenderer'
import { authorization } from '../../../../../Module/Auth/Auth'

/**
 *
 * @param {array} props.missions Missions that should be rendered into the table
 * @param {array} props.checkboxesMap Hashmap that indicates which table rows (missions) are checked
 * @param {array} props.onCheckboxChange Function that will be called on checkbox change
 *
 * @returns {JSX.Element} Missions table with filters, search and sortable columns
 *
 * @example
 * <LetterboxMissionsTable
 *   missions={[{ id: 10022, density: 1, ...mission }]}
 *   checkboxesMap={{ 10022: true, 12320: false }}
 *   onCheckboxChange={() => {}}
 * />
 */
const LetterboxMissionsTable = ({
  missions,
  campaign,
  checkboxesMap,
  onCheckboxChange,
  onTableDataUpdate,
  onDelete,
  onEditMeetingPoint,
  timezone,
}) => {
  const isCityManagerAssistant = authorization.isCityManagerAssistant()

  const postRowRender = mission => {
    return letterboxMissionsTableRowRenderer({
      id: mission.id,
      label: mission.label,
      onCheckboxChange,
      cancelled: mission.cancelled,
      status: mission['Status'],
      isReadyToPublish: mission.isReadyToPublish,
      density: mission['Density'],
      addresses: mission['Addresses'],
      payAdjustment: mission['Pay Adjustment'],
      bonusPerConversion: mission['Bonus per conversion'],
      bonusThreshold: mission['Bonus threshold'],
      costPerFlyer: mission['Cost Per Flyer'],
      flyerCode: mission['Flyer Code'],
      totalFlyersDistributed: mission['Flyers Distributed'],
      packSize: mission['Pack Size'],
      week: mission['Week'],
      dateTime: mission['Date'],
      isChecked: Boolean(checkboxesMap[mission.id]),
      baName: mission['BA'],
      baId: mission['userId'],
      cityId: mission['cityId'],
      meetingPoint: mission['Meeting Point'],
      onEditMeetingPoint,
      onDelete,
      timezone,
      boundary: mission.boundary,
    })
  }

  const tableHeadersUpdated = useMemo(() => {
    if (isCityManagerAssistant) {
      return tableHeaders.filter(
        item =>
          ![
            'Pay Adjustment',
            'Bonus per conversion',
            'Bonus threshold',
          ].includes(item),
      )
    }

    if (authorization.isClientRole()) {
      return tableHeaders.filter(
        item =>
          ![
            'Density',
            'Addresses',
            'Pay Adjustment',
            'Bonus per conversion',
            'Bonus threshold',
            'Cost Per Flyer',
            'Date',
          ].includes(item),
      )
    }

    if (!campaign.bonusEnabled) {
      return tableHeaders.filter(
        item => !['Bonus per conversion', 'Bonus threshold'].includes(item),
      )
    }

    return tableHeaders
  }, [tableHeaders])

  const formattedData = formatData(missions)

  return (
    <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
      <SimpleTableTable
        header={tableHeadersUpdated}
        data={formattedData}
        filterOn={filterableColumns}
        sortOn={sortableColumns}
        postRowRender={postRowRender}
        footerRender={getFooterData}
        showPages={false}
        onTableDataUpdate={onTableDataUpdate}
      />
    </div>
  )
}

export default LetterboxMissionsTable
